import React, { useEffect, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import saApi from "../lib/saApi";
import { toast } from "sonner";
import { Loader2, Save, Settings2, ShieldOff, Palette, Megaphone, Image as ImageIcon, Mail, Send, Newspaper, DatabaseZap, CheckCircle2, AlertTriangle, RefreshCw, History, HardDriveDownload, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useSuperAdminAuth } from "../context/SuperAdminAuthContext";

export default function SASettings() {
  const [g, setG] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { saApi.get("/settings").then(({ data }) => setG(data)).catch(() => setG({})); }, []);

  const patch = (k, v) => setG((s) => ({ ...s, [k]: v }));
  const patchFlag = (k, v) => setG((s) => ({ ...s, feature_flags: { ...(s.feature_flags || {}), [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      const values = {
        platform_name: g.platform_name,
        support_email: g.support_email,
        smtp_fallback_enabled: !!g.smtp_fallback_enabled,
        universal_theme: g.universal_theme,
        default_logo_url: g.default_logo_url,
        default_primary_color: g.default_primary_color,
        default_accent_color: g.default_accent_color,
        feature_flags: g.feature_flags,
        signup_disabled: !!g.signup_disabled,
        banner_message: g.banner_message,
      };
      const { data } = await saApi.put("/settings", { values });
      setG(data);
      toast.success("Global settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  if (!g) {
    return (
      <SuperAdminShell title="Global Settings">
        <div className="px-5 md:px-8 py-8 text-white/60 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />Loading…
        </div>
      </SuperAdminShell>
    );
  }

  return (
    <SuperAdminShell
      title="Global Settings"
      actions={
        <Button data-testid="sa-settings-save" onClick={save} disabled={busy}
                className="text-white" style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Save</>}
        </Button>
      }
    >
      <div className="px-5 md:px-8 py-6 space-y-6 max-w-4xl">
        <Section icon={Settings2} title="Platform identity" tint="#22D3EE">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Platform name">
              <Input data-testid="sa-settings-platform-name" value={g.platform_name || ""}
                     onChange={(e) => patch("platform_name", e.target.value)}
                     className="bg-white/5 border-white/10 text-white" />
            </Field>
            <Field label="Support email">
              <Input data-testid="sa-settings-support-email" value={g.support_email || ""}
                     onChange={(e) => patch("support_email", e.target.value)}
                     className="bg-white/5 border-white/10 text-white" placeholder="support@example.com" />
            </Field>
            <Field label="Universal default theme">
              <Select value={g.universal_theme || "light"} onValueChange={(v) => patch("universal_theme", v)}>
                <SelectTrigger data-testid="sa-settings-theme" className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </Section>

        <Section icon={ImageIcon} title="Universal branding defaults" tint="#F472B6">
          <p className="text-[11px] text-white/50 mb-3">
            Applied to any tenant that hasn&apos;t uploaded its own logo or picked its
            own colours. Tenant-level values always win.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Default logo URL">
              <Input data-testid="sa-settings-default-logo" value={g.default_logo_url || ""}
                     onChange={(e) => patch("default_logo_url", e.target.value)}
                     placeholder="https://cdn.example.com/logo.png"
                     className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />
            </Field>
            <Field label="Default primary colour">
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  data-testid="sa-settings-default-primary-picker"
                  value={g.default_primary_color || "#2B4EFF"}
                  onChange={(e) => patch("default_primary_color", e.target.value)}
                  className="w-10 h-10 rounded-lg border border-white/10 bg-transparent cursor-pointer shrink-0"
                />
                <Input data-testid="sa-settings-default-primary" value={g.default_primary_color || ""}
                       onChange={(e) => patch("default_primary_color", e.target.value)}
                       placeholder="#2B4EFF"
                       className="bg-white/5 border-white/10 text-white font-mono placeholder:text-white/30" />
              </div>
            </Field>
            <Field label="Default accent colour">
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  data-testid="sa-settings-default-accent-picker"
                  value={g.default_accent_color || "#FF6B35"}
                  onChange={(e) => patch("default_accent_color", e.target.value)}
                  className="w-10 h-10 rounded-lg border border-white/10 bg-transparent cursor-pointer shrink-0"
                />
                <Input data-testid="sa-settings-default-accent" value={g.default_accent_color || ""}
                       onChange={(e) => patch("default_accent_color", e.target.value)}
                       placeholder="#FF6B35"
                       className="bg-white/5 border-white/10 text-white font-mono placeholder:text-white/30" />
              </div>
            </Field>
          </div>
          {(g.default_logo_url || g.default_primary_color || g.default_accent_color) && (
            <div className="mt-4 rounded-xl p-4 border border-white/10 bg-white/5 flex items-center gap-4">
              {g.default_logo_url ? (
                <img src={g.default_logo_url} alt="logo" className="h-8 w-auto rounded" />
              ) : (
                <span className="text-xs text-white/40">No logo</span>
              )}
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-md border border-white/20" style={{ background: g.default_primary_color || "transparent" }} />
                <span className="w-6 h-6 rounded-md border border-white/20" style={{ background: g.default_accent_color || "transparent" }} />
                <span className="text-[11px] uppercase tracking-widest text-white/50 ml-1">Preview</span>
              </div>
            </div>
          )}
        </Section>

        <Section icon={Palette} title="Feature flags" tint="#A78BFA">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {["ai_gateway", "schema_drift", "impossible_travel"].map((k) => (
              <FlagRow
                key={k}
                testid={`sa-settings-flag-${k}`}
                label={k.replace(/_/g, " ")}
                checked={!!g.feature_flags?.[k]}
                onChange={(v) => patchFlag(k, v)}
              />
            ))}
          </div>
        </Section>

        <Section icon={ShieldOff} title="Access controls" tint="#F87171">
          <FlagRow
            testid="sa-settings-signup-disabled"
            label="Disable public signups"
            hint="When on, /signup returns 403 for every tenant. Only invited users can join."
            checked={!!g.signup_disabled}
            onChange={(v) => patch("signup_disabled", v)}
          />
          <FlagRow
            testid="sa-settings-smtp-fallback"
            label="Enable platform SMTP fallback"
            hint="If a tenant hasn't configured SMTP, fall back to the platform's server."
            checked={!!g.smtp_fallback_enabled}
            onChange={(v) => patch("smtp_fallback_enabled", v)}
          />
        </Section>

        <Section icon={Megaphone} title="Broadcast" tint="#FBBF24">
          <Field label="Global banner message">
            <Input data-testid="sa-settings-banner" value={g.banner_message || ""}
                   onChange={(e) => patch("banner_message", e.target.value)}
                   placeholder="Scheduled maintenance Saturday 02:00 UTC…"
                   className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />
          </Field>
          <p className="text-[11px] text-white/50">
            Shown live at the top of every client workspace when set.
          </p>
        </Section>

        <CockpitDigestSection />

        <UpgradeSection />

        {g.updated_at && (
          <p className="text-[11px] text-white/40">Last updated {new Date(g.updated_at).toLocaleString()}</p>
        )}
      </div>
    </SuperAdminShell>
  );
}

function Section({ icon: Icon, title, tint, children }) {
  return (
    <div className="rounded-2xl p-5 border border-white/10 backdrop-blur-md space-y-3"
         style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))" }}>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${tint}22` }}>
          <Icon className="w-4 h-4" style={{ color: tint }} />
        </div>
        <h3 className="font-display text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-white/60 uppercase tracking-widest">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function FlagRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl p-3 border border-white/10 bg-white/5">
      <div className="min-w-0">
        <div className="text-sm text-white capitalize">{label}</div>
        {hint && <div className="text-[11px] text-white/40 mt-0.5">{hint}</div>}
      </div>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function CockpitDigestSection() {
  const [f, setF] = useState(null);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const savedHourRef = React.useRef(null);

  useEffect(() => {
    saApi.get("/cockpit-digest")
      .then(({ data }) => {
        setF({ enabled: !!data.enabled, weekday: data.weekday ?? 0, hour_utc: data.hour_utc ?? 8, last_sent_week: data.last_sent_week });
        savedHourRef.current = data.hour_utc ?? 8;
      })
      .catch(() => setF({ enabled: false, weekday: 0, hour_utc: 8 }));
  }, []);

  const save = async (patch) => {
    const next = { ...f, ...patch };
    setF(next);
    try {
      await saApi.put("/cockpit-digest", { enabled: next.enabled, weekday: next.weekday, hour_utc: next.hour_utc });
      savedHourRef.current = next.hour_utc;
      toast.success("Cockpit digest settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const saveHourIfChanged = () => {
    if (f.hour_utc !== savedHourRef.current) save({});
  };
  const sendNow = async () => {
    setSending(true);
    try { const { data } = await saApi.post("/cockpit-digest/send-now"); toast.success(`Digest sent (${data.detail})`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };
  const doPreview = async () => {
    setPreviewing(true); setPreview(null);
    try { const { data } = await saApi.post("/cockpit-digest/preview"); setPreview(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setPreviewing(false); }
  };

  if (!f) return null;

  return (
    <Section icon={Mail} title="Weekly cockpit digest" tint="#22D3EE">
      <p className="text-[11px] text-white/50 mb-3">
        A weekly email to you (the signed-in super admin): tenant/dashboard activity, AI spend, and
        failed logins across every tenant in your scope, last 7 days.
      </p>
      <div className="flex items-center gap-4 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-white">
          <Switch data-testid="sa-digest-toggle" checked={f.enabled} onCheckedChange={(v) => save({ enabled: v })} />Send weekly
        </label>
        <Select value={String(f.weekday)} onValueChange={(v) => save({ weekday: Number(v) })}>
          <SelectTrigger data-testid="sa-digest-weekday" className="w-[130px] h-8 bg-white/5 border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-white/50">Hour (UTC)</Label>
          <Input data-testid="sa-digest-hour" type="number" min={0} max={23} className="w-20 h-8 bg-white/5 border-white/10 text-white"
                 value={f.hour_utc} onChange={(e) => setF((x) => ({ ...x, hour_utc: Number(e.target.value) || 0 }))}
                 onBlur={saveHourIfChanged} />
        </div>
        <span className="flex-1" />
        <Button data-testid="sa-digest-preview-btn" size="sm" variant="outline" onClick={doPreview} disabled={previewing}
                className="bg-white/5 border-white/10 text-white hover:bg-white/10">
          {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Newspaper className="w-4 h-4 mr-1" />}Preview
        </Button>
        <Button data-testid="sa-digest-send-btn" size="sm" onClick={sendNow} disabled={sending}
                className="text-white" style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}Send now
        </Button>
      </div>
      {f.last_sent_week && <p className="text-[11px] text-white/40 mt-2">Last sent: week {f.last_sent_week}</p>}
      {preview && (
        <div className="mt-4 rounded-xl border border-white/10 overflow-hidden" data-testid="sa-digest-preview">
          <div className="px-3 py-2 text-xs font-semibold text-white bg-white/5 border-b border-white/10">{preview.subject}</div>
          <iframe title="cockpit digest preview" srcDoc={preview.html} className="w-full h-80 bg-white" sandbox="" />
        </div>
      )}
    </Section>
  );
}

const fmtTs = (s) => (s ? new Date(s).toLocaleString() : "—");
const fmtBytes = (n) => (n == null ? "—" : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`);

function UpgradeSection() {
  const { sa } = useSuperAdminAuth();
  const [st, setSt] = useState(null);
  const [err, setErr] = useState(null);
  const [running, setRunning] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [withBackup, setWithBackup] = useState(true);

  const backupNow = async () => {
    setBackingUp(true);
    try {
      const { data } = await saApi.post("/upgrade/backup", {});
      toast.success(`Backup written: ${data.file} (${fmtBytes(data.size_bytes)})`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Backup failed"); }
    finally { setBackingUp(false); }
  };
  const downloadBackup = (b) => {
    const base = saApi.defaults.baseURL || "";
    window.open(`${base}/upgrade/backups/${b.id}/download`, "_blank");
  };

  const load = () => saApi.get("/upgrade/status").then(({ data }) => { setSt(data); setErr(null); })
    .catch((e) => setErr(e?.response?.status === 403 ? "forbidden" : "error"));
  useEffect(() => { load(); }, []);

  const run = async () => {
    setConfirm(false); setRunning(true); setResult(null);
    try {
      const { data } = await saApi.post("/upgrade/run", { backup: withBackup });
      setResult(data);
      if (data.ok) toast.success(`Database upgraded to v${data.to_version}${data.backup ? ` · backup ${data.backup.file}` : ""}`);
      else toast.error("Upgrade stopped — a script failed. See details below.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upgrade failed"); }
    finally { setRunning(false); }
  };

  if (err === "forbidden") return null;
  if (!st) {
    return (
      <Section icon={DatabaseZap} title="Upgrade" tint="#F59E0B">
        <p className="text-[11px] text-white/50" data-testid="sa-upgrade-loading">{err ? "Could not load upgrade status." : "Loading…"}</p>
      </Section>
    );
  }
  const pending = st.pending || [];
  const isMaster = !!sa?.is_master;
  return (
    <Section icon={DatabaseZap} title="Upgrade" tint="#F59E0B">
      <p className="text-[11px] text-white/50">
        Each build ships its database migration scripts. Compare the database version with the scripts in this
        build and apply the pending ones in order. Scripts are idempotent and stop at the first failure.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="sa-upgrade-versions">
        <VersionTile label="System version" value={st.system_version} sub={st.build_id ? `build ${st.build_id}` : `booted ${fmtTs(st.last_boot_at)}`} testid="sa-upgrade-system-version" />
        <VersionTile label="Schema in this build" value={`v${st.code_schema_version}`} sub={`${(st.scripts || []).length} script${(st.scripts || []).length === 1 ? "" : "s"} shipped`} testid="sa-upgrade-code-version" />
        <VersionTile label="Database version" value={`v${st.db_schema_version}`} sub={st.last_upgrade_at ? `upgraded ${fmtTs(st.last_upgrade_at)}` : "never upgraded"} testid="sa-upgrade-db-version" />
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {st.up_to_date ? (
          <span data-testid="sa-upgrade-status" className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/20">
            <CheckCircle2 className="w-3.5 h-3.5" />Database is up to date
          </span>
        ) : (
          <span data-testid="sa-upgrade-status" className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/20">
            <AlertTriangle className="w-3.5 h-3.5" />{pending.length} migration{pending.length === 1 ? "" : "s"} pending
          </span>
        )}
        {st.lock?.held && <span className="text-[11px] text-white/50">Upgrade in progress by {st.lock.by}…</span>}
        <span className="flex-1" />
        <Button data-testid="sa-upgrade-refresh" size="sm" variant="outline" onClick={load} className="bg-white/5 border-white/10 text-white hover:bg-white/10">
          <RefreshCw className="w-4 h-4 mr-1" />Refresh
        </Button>
        <Button data-testid="sa-upgrade-backup" size="sm" variant="outline" onClick={backupNow} disabled={backingUp || !isMaster}
                title={!isMaster ? "Master super admin required" : "Write a full MongoDB dump now"}
                className="bg-white/5 border-white/10 text-white hover:bg-white/10">
          {backingUp ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <HardDriveDownload className="w-4 h-4 mr-1" />}Backup now
        </Button>
        <Button data-testid="sa-upgrade-history-toggle" size="sm" variant="outline" onClick={() => setShowHistory((v) => !v)} className="bg-white/5 border-white/10 text-white hover:bg-white/10">
          <History className="w-4 h-4 mr-1" />{showHistory ? "Hide history" : `History (${(st.history || []).length})`}
        </Button>
        <Button data-testid="sa-upgrade-run" size="sm" onClick={() => setConfirm(true)} disabled={running || st.up_to_date || !isMaster || st.lock?.held}
                title={!isMaster ? "Master super admin required" : undefined}
                className="text-white" style={{ background: "linear-gradient(135deg,#F59E0B,#EF4444)" }}>
          {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <DatabaseZap className="w-4 h-4 mr-1" />}Upgrade database
        </Button>
      </div>
      {!isMaster && !st.up_to_date && <p className="text-[11px] text-amber-300/80">Only a master super admin can run the upgrade.</p>}

      <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="sa-upgrade-scripts">
        <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-white/50 bg-white/5 border-b border-white/10">Migration scripts in this build</div>
        <div className="divide-y divide-white/5 max-h-64 overflow-auto">
          {(st.scripts || []).slice().reverse().map((s) => (
            <div key={s.version} data-testid={`sa-upgrade-script-${s.version}`} className="flex items-start gap-3 px-3 py-2 text-xs">
              <span className={"mt-0.5 inline-flex shrink-0 w-14 justify-center rounded-md px-1.5 py-0.5 font-mono " + (s.applied ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300")}>v{s.version}</span>
              <div className="min-w-0 flex-1">
                <div className="text-white font-medium">{s.name} <span className="text-white/30 font-normal">· {s.module}.py</span></div>
                {s.description && <div className="text-white/50 mt-0.5">{s.description}</div>}
              </div>
              <span className={"shrink-0 text-[10px] uppercase tracking-wide " + (s.applied ? "text-emerald-300/80" : "text-amber-300/80")}>{s.applied ? "applied" : "pending"}</span>
            </div>
          ))}
          {!(st.scripts || []).length && <p className="px-3 py-3 text-xs text-white/40">No migration scripts shipped with this build.</p>}
        </div>
      </div>

      {result && (
        <div data-testid="sa-upgrade-result" className={"rounded-xl border p-3 text-xs space-y-1 " + (result.ok ? "border-emerald-400/20 bg-emerald-500/10" : "border-red-400/20 bg-red-500/10")}>
          <div className="text-white font-medium">{result.ok ? `Upgraded v${result.from_version} → v${result.to_version}` : `Stopped at a failure (database now at v${result.to_version})`}</div>
          {(result.results || []).map((r) => (
            <div key={r.version} className="flex items-start gap-2 text-white/80">
              <span className="font-mono">v{r.version}</span><span>{r.name}</span>
              <span className={r.status === "applied" ? "text-emerald-300" : "text-red-300"}>{r.status}</span>
              <span className="text-white/40">{r.duration_ms} ms</span>
              {r.error && <span className="text-red-200 break-all">{r.error}</span>}
              {r.summary && typeof r.summary === "object" && <span className="text-white/50">{Object.entries(r.summary).map(([k, v]) => `${k}=${v}`).join(" · ")}</span>}
            </div>
          ))}
          {!(result.results || []).length && <div className="text-white/60">Nothing to apply.</div>}
        </div>
      )}

      <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="sa-upgrade-backups">
        <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-white/50 bg-white/5 border-b border-white/10 flex items-center justify-between">
          <span>Database backups</span><span className="normal-case tracking-normal text-white/40">{st.backup_dir}</span>
        </div>
        <div className="divide-y divide-white/5 max-h-48 overflow-auto">
          {(st.backups || []).map((b) => (
            <div key={b.id} data-testid={`sa-backup-row-${b.id}`} className="flex items-center gap-3 px-3 py-1.5 text-xs">
              <span className={"shrink-0 w-2 h-2 rounded-full " + (b.status === "ok" ? "bg-emerald-400" : b.status === "failed" ? "bg-red-400" : "bg-white/30")} />
              <span className="font-mono text-white/80 truncate flex-1">{b.file}</span>
              <span className="text-white/40 shrink-0">{b.reason}</span>
              <span className="text-white/40 shrink-0">{fmtBytes(b.size_bytes)}</span>
              <span className="text-white/40 shrink-0">{fmtTs(b.created_at)}</span>
              {b.status === "ok" && isMaster && (
                <button data-testid={`sa-backup-download-${b.id}`} onClick={() => downloadBackup(b)} title="Download" className="text-cyan-300 hover:text-cyan-200 shrink-0"><Download className="w-3.5 h-3.5" /></button>
              )}
              {b.error && <span className="text-red-300 truncate max-w-[200px]" title={b.error}>{b.error}</span>}
            </div>
          ))}
          {!(st.backups || []).length && <p className="px-3 py-3 text-xs text-white/40">No backups yet. One is taken automatically before every upgrade.</p>}
        </div>
      </div>

      {showHistory && (
        <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="sa-upgrade-history">
          <table className="w-full text-xs">
            <thead><tr className="text-[10px] uppercase tracking-widest text-white/50 bg-white/5">
              <th className="text-left px-3 py-2">Version</th><th className="text-left px-3 py-2">Script</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Applied</th><th className="text-left px-3 py-2">By</th><th className="text-right px-3 py-2">Time</th>
            </tr></thead>
            <tbody className="divide-y divide-white/5">
              {(st.history || []).map((h) => (
                <tr key={`${h.version}-${h.started_at}`} className="text-white/80">
                  <td className="px-3 py-1.5 font-mono">v{h.version}</td>
                  <td className="px-3 py-1.5">{h.name}{h.error && <div className="text-red-300 text-[10px] break-all">{h.error}</div>}</td>
                  <td className={"px-3 py-1.5 " + (h.status === "applied" ? "text-emerald-300" : "text-red-300")}>{h.status}</td>
                  <td className="px-3 py-1.5 text-white/60">{fmtTs(h.finished_at || h.started_at)}</td>
                  <td className="px-3 py-1.5 text-white/60">{h.applied_by}</td>
                  <td className="px-3 py-1.5 text-right text-white/50">{h.duration_ms} ms</td>
                </tr>
              ))}
              {!(st.history || []).length && <tr><td colSpan={6} className="px-3 py-3 text-white/40">No migrations have been applied yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="sa-upgrade-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Upgrade the database to v{st.code_schema_version}?</AlertDialogTitle>
            <AlertDialogDescription>
              {pending.length} pending script{pending.length === 1 ? "" : "s"} will run in order: {pending.map((p) => `v${p.version} ${p.name}`).join(", ")}.
              The run stops at the first failure and can be retried.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="sa-upgrade-backup-toggle">
            <input type="checkbox" checked={withBackup} onChange={(e) => setWithBackup(e.target.checked)} className="h-4 w-4" />
            Back up the database first (recommended)
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sa-upgrade-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="sa-upgrade-confirm-run" onClick={run} className="bg-amber-600 hover:bg-amber-700 text-white">Run upgrade</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  );
}

function VersionTile({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl p-3 border border-white/10 bg-white/5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
      <div className="font-display text-xl text-white mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-white/40 mt-0.5">{sub}</div>}
    </div>
  );
}
